import { Component, ViewEncapsulation, OnInit, ElementRef, ViewChild, QueryList, ViewChildren, Output, EventEmitter, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatExpansionModule } from '@angular/material/expansion';
import { NavbarComponent } from "../navbar/navbar.component";
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Color, NgxChartsModule, ScaleType } from '@swimlane/ngx-charts';
import { LegendPosition } from '@swimlane/ngx-charts';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { UploadService } from '../../services/upload.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DragDropModule, CdkDragDrop, moveItemInArray, transferArrayItem, CdkDragMove, CdkDragRelease, CdkDragStart } from '@angular/cdk/drag-drop';
import { CdkDragEnter } from '@angular/cdk/drag-drop';
import { MatExpansionPanel } from '@angular/material/expansion';
import { Renderer2 } from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { LoaderComponent } from "../../utility components/loader/loader.component";
import { ExploreModeService } from '../../services/explore_mode.service';
import { EditTtmDialogComponent } from '../edit-ttm-dialog/edit-ttm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { TTMMapResponse } from '../../models/ttm-map.model';

@Component({
  selector: 'app-explore-mode-output',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatExpansionModule,
    MatProgressBarModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    NavbarComponent,
    ReactiveFormsModule,
    NgxChartsModule,
    MatTooltipModule,
    DragDropModule,
    MatExpansionPanel,
    MatCheckboxModule,
    MatRadioModule,
    FormsModule,
    LoaderComponent,
    LoaderComponent,
    EditTtmDialogComponent
  ],
  templateUrl: './explore-mode-output.component.html',
  styleUrls: ['./explore-mode-output.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('expandCollapse', [
      state('collapsed', style({ height: '0px', opacity: 0 })),
      state('expanded', style({ height: '*', opacity: 1 })),
      transition('collapsed <=> expanded', animate('300ms ease-in-out'))
    ])
  ],
})
export class ExploreModeOutputComponent implements OnInit {
  @ViewChild('relevantPanel') relevantPanel!: MatExpansionPanel;
  @ViewChild('nonRelevantPanel') nonRelevantPanel!: MatExpansionPanel;
  @ViewChildren('scrollContainer') scrollContainers!: QueryList<ElementRef>;
  @ViewChild('relevantPanelHeader', { read: ElementRef }) relevantPanelHeader!: ElementRef;
  @ViewChild('nonRelevantPanelHeader', { read: ElementRef }) nonRelevantPanelHeader!: ElementRef;
  @Output() loadingStateChange = new EventEmitter<boolean>();

  private initialColumns: { name: string; subColumns: any[]; expanded: boolean; id: string }[] = [];
  private initialNonRelevantColumns: string[] = [];
  isExpanded = false;
  files: string[] = [];
  selectedFile = new FormControl('');
  driThreshold: any = 0;
  dqiThreshold: any = 0;
  driAverage: number = 0;
  dqiAverage: number = 0;
  columns: { name: string; subColumns: any[]; expanded: boolean; id: string }[] = [];
  nonRelevantColumns: any[] = [];
  chartData: any[] = [];
  responseData: any = null;

  view: [number, number] = [800, 420];
  hoveredBar: any = null;
  tooltipPosition = { x: 0, y: 0 };

  dropListIds: string[] = [];
  private scrollSpeed = 5;
  private scrollInterval: any = null;
  private dragSourceId: string | null = null;

  showXAxis: boolean = true;
  showYAxis: boolean = true;
  gradient: boolean = false;
  showLegend: boolean = false;
  legendPosition: LegendPosition = LegendPosition.Right;
  showXAxisLabel: boolean = true;
  xAxisLabel: string = 'Columns';
  showYAxisLabel: boolean = true;
  yAxisLabel: string = 'Percentage';
  legendTitle: string = 'Legend';

  selectedSubColsMap: { [key: string]: string } = {};
  apiPayloadFromDialog: any = null;

  colorScheme: string | Color = {
    domain: ['#A7C7FC', '#F9D8C4', '#F7C8E0', '#B5EAD7', '#FFD791'],
    name: 'custom',
    selectable: true,
    group: ScaleType.Ordinal
  };
  data: any;
  fileName = '';
  isButtonDisabled = false;
  isLoading = false;
  ttmMapResponse: TTMMapResponse | null = null;



  constructor(private dialog: MatDialog, private uploadService: UploadService, private exploreModeService: ExploreModeService, private renderer: Renderer2) { }

  ngOnInit() {
    this.adjustChartSize();
    this.initialize();
  }


  @HostListener('window:resize', ['$event'])
  onResize() {
    this.adjustChartSize();
  }

  private adjustChartSize() {
    const screenHeight = window.innerHeight;
    const screenWidth = window.innerWidth;

    this.view = [screenWidth * 0.55, screenHeight * 0.6]; // Adjust width & height dynamically
  }



  initialize() {
    this.uploadService.getResponseData().subscribe((data) => {
      if (data) {
        this.responseData = data;
        console.log('Response Data:', this.responseData);

        if (this.responseData.files_received && this.responseData.files_received.length > 0) {
          this.files = this.responseData.files_received;
          this.selectedFile.setValue(this.files[0], { emitEvent: false });
          this.fileName = this.files[0];
          this.data = this.responseData?.explore_mode?.Inbound[this.getMappedFileName(this.files[0])];
          if (this.data) {
            console.log('this.data set:', this.data);
            this.updateThresholds(this.fileName);
            this.updateColumnNGraph();
            this.initializeDefaultSelections();
          } else {
            console.error('Data not found for file:', this.getMappedFileName(this.fileName));
          }
        } else {
          console.warn('No files received in response data');
          this.files = [];
          this.selectedFile.setValue('');
          this.data = null;
        }

        // Set up valueChanges subscription after initial setup
        this.selectedFile.valueChanges.subscribe((fileName) => {
          if (fileName) {
            this.fileName = fileName;
            this.data = this.responseData?.explore_mode?.Inbound[this.getMappedFileName(fileName)];
            if (this.data) {
              console.log('this.data updated for file:', this.getMappedFileName(fileName), this.data);
              this.updateThresholds(fileName);
              this.updateColumnNGraph();
            } else {
              console.error('Data not found for file:', this.getMappedFileName(fileName));
            }
          }
        });
      }
    });
  }


  initializeDefaultSelections() {
    this.columns.forEach(column => {
      column.subColumns.forEach((subCol: any) => {
        subCol.overridden_recommended = subCol.overridden_recommended || false;
      });
      const recommendedSubCol = column.subColumns.find(subCol => subCol.final_recommended);
      if (recommendedSubCol) {
        this.selectedSubColsMap[column.name] = recommendedSubCol.name;
      }
    });
  }


  updateThresholds(fileName: string) {
    // Normalize the filename (replace spaces with underscores)
    const normalizedFileName = this.getMappedFileName(fileName);

    let fileData;

    // Look for the file in 'explore_mode' first
    if (this.responseData?.explore_mode?.Inbound && this.responseData.explore_mode.Inbound[normalizedFileName]) {
      fileData = this.responseData.explore_mode.Inbound[normalizedFileName];
    }
    // If not found, check in 'Inbound'
    else if (this.responseData?.Inbound && this.responseData.Inbound[normalizedFileName]) {
      fileData = this.responseData.Inbound[normalizedFileName];
    }

    // If file data is found, update the threshold values
    if (fileData) {
      this.driThreshold = fileData["Data_Relevancy_Index_Threshold"] || 0;
      this.dqiThreshold = fileData["Data_Quality_Index_Threshold"] || 0;
      this.driAverage = fileData["Average_Data_Relevancy_Index_Per_File"] || 0;
      this.dqiAverage = fileData["Average_Data_Quality_Index_Per_File"] || 0;
    } else {
      console.log("File not found in Inbound or explore_mode:", fileName);
    }
  }

  // Mapping function to replace spaces with underscores
  getMappedFileName(fileName: string): string {
    // Replace spaces with underscores and handle '(1)' to '_1'
    const normalized = fileName
      .replace(/ /g, '_')
      .replace(/\(1\)/g, '_1');
    console.log('Normalized file name:', fileName, '->', normalized);
    return normalized;
  }



  updateColumnNGraph() {
    if (!this.data || !this.responseData || !this.responseData.explore_mode) {
      console.error('Cannot update column and graph: data or responseData is undefined');
      return;
    }

    this.columns = [];
    this.chartData = [];
    console.log('reached');
    console.log('this.data', this.data);
    if (this.data?.Irrelevant_Columns) {
      this.nonRelevantColumns = this.data.Irrelevant_Columns || [];
      console.log('Non-Relevant Columns:', this.nonRelevantColumns);
    }
    console.log('out', this.nonRelevantColumns);
    this.dropListIds = ['non-relevant'];
    Object.entries(this.data.Dexcat_Output).forEach(([key, category]: [string, any], index) => {
      const subColumns = category.map((item: any) => ({
        col: item.col,
        recommended: item.recommended,
        overridden_recommended: item.overridden_recommended || false,
        final_recommended: item.final_recommended,
        dri: item.dri,
        dqi: item.dqi,
        sample_values: item.sample_values
      }));

      const columnId = `relevant-${index}`;
      this.columns.push({
        name: key,
        subColumns: subColumns,
        expanded: false,
        id: columnId
      });
      this.dropListIds.push(columnId);

      const chartSeries = category
        .filter((item: any) => item.final_recommended)
        .map((item: any) => ({
          name: key,
          series: [
            { name: 'DRI', value: item.dri, extra: { item: item.col, sampleValues: item.sample_values } },
            { name: 'DQI', value: item.dqi, extra: { item: item.col, sampleValues: item.sample_values } }
          ],
        }));

      this.chartData.push(...chartSeries);
    });

    this.initialColumns = JSON.parse(JSON.stringify(this.columns));
    this.initialNonRelevantColumns = [...this.nonRelevantColumns];
  }

  resetDashboard() {
    this.columns = JSON.parse(JSON.stringify(this.initialColumns));
    this.nonRelevantColumns = [...this.initialNonRelevantColumns];
    this.columns.forEach(column => column.expanded = false);
    this.selectedSubColsMap = {};
    this.columns.forEach(column => {
      const recommendedSubCol = column.subColumns.find(subCol => subCol.final_recommended);
      if (recommendedSubCol) {
        this.selectedSubColsMap[column.name] = recommendedSubCol.name;
      }
    });
    console.log('Side Panel reset. Checkboxes restored to initial recommendations.');
  }

  reCalculate() {
    this.isLoading = true;
    if (!this.data || !this.fileName) {
      console.error('Cannot recalculate: data or fileName is undefined');
      this.isLoading = false;
      return;
    }

    let data = { ...this.data };
    data.Irrelevant_Columns = this.nonRelevantColumns;
    data.Dexcat_Output = this.formatDexcatOutput(this.columns);
    const payload = { ...this.responseData };
    const mappedFileName = this.getMappedFileName(this.fileName);
    const sessionid: any = localStorage.getItem('sessionid')
    payload.session_id = sessionid;
    payload.explore_mode = payload.explore_mode || {};
    payload.explore_mode.Inbound = payload.explore_mode.Inbound || {};
    payload.explore_mode.Inbound[mappedFileName] = data;

    this.uploadService.getUpdatedDashboard(payload).subscribe({
      next: (resp: any) => {
        this.isLoading = false;
        if (resp && resp.updated_response && resp.updated_response.explore_mode && resp.updated_response.explore_mode.Inbound) {
          const result = resp.updated_response.explore_mode.Inbound[mappedFileName];
          if (result) {
            this.driAverage = result.Average_Data_Relevancy_Index_Per_File || 0;
            this.dqiAverage = result.Average_Data_Quality_Index_Per_File || 0;
            this.data = result;
            this.responseData.explore_mode.Inbound[mappedFileName] = result; // Update responseData
            this.updateColumnNGraph();
          } else {
            console.error('Recalculate failed: result not found for file:', mappedFileName);
          }
        } else {
          console.error('Recalculate failed: invalid response structure', resp);
        }
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Recalculate error:', err);
      }
    });
  }

  formatDexcatOutput(data: any[]): any {
    return data.reduce((acc, item) => {
      acc[item.name] = (acc[item.name] || []).concat(
        item.subColumns.map((subitem: any) => ({
          col: subitem.col,
          dqi: subitem.dqi,
          dri: subitem.dri,
          recommended: subitem.recommended,
          final_recommended: subitem.final_recommended,
          overridden_recommended: subitem.overridden_recommended,
          sample_values: subitem.sample_values || []
        }))
      );
      return acc;
    }, {});
  }


  dragEntered(event: CdkDragEnter<any[]>) {
    const targetId = event.container.id;
    console.log('Drag entered target ID:', targetId);

    // Expand column only if dragging from non-relevant to relevant
    const targetColumn = this.columns.find(col => col.id === targetId);
    if (
      targetColumn &&
      !targetColumn.expanded &&
      this.dragSourceId === 'non-relevant' // Only expand if dragging from non-relevant
    ) {
      console.log(`Expanding column: ${targetColumn.name} on drag enter (from non-relevant)`);
      this.toggleColumn(targetColumn);
    }

    // Expand relevant panel if dragging from non-relevant
    if (
      targetId.startsWith('relevant') &&
      !this.relevantPanel.expanded &&
      this.dragSourceId === 'non-relevant'
    ) {
      console.log('Expanding relevant panel on drag enter (from non-relevant)');
      this.relevantPanel.open();
    }
    // Expand non-relevant panel if dragging from relevant
    else if (
      targetId === 'non-relevant' &&
      !this.nonRelevantPanel.expanded &&
      this.dragSourceId !== null &&
      this.dragSourceId.startsWith('relevant')
    ) {
      console.log('Expanding non-relevant panel on drag enter (from relevant)');
      this.nonRelevantPanel.open();
    }
  }

  drop(event: CdkDragDrop<any[]>) {
    if (event.previousContainer === event.container) {
      console.log('Reordering within same list is not allowed');
      return;
    }

    const fromRelevant = event.previousContainer.id.startsWith('relevant');
    const toRelevant = event.container.id.startsWith('relevant');
    const fromNonRelevant = event.previousContainer.id === 'non-relevant';
    const toNonRelevant = event.container.id === 'non-relevant';

    if (fromRelevant && toRelevant) {
      console.log('Transfer between relevant columns is not allowed');
      return;
    }

    if (fromNonRelevant && toNonRelevant) {
      console.log('Transfer within non-relevant is not allowed');
      return;
    }

    const item = event.previousContainer.data[event.previousIndex];
    console.log('Item being dropped:', item);

    if (fromNonRelevant && toRelevant) {
      console.log('Moving from non-relevant to relevant');
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
      const transferredItem = event.container.data[event.currentIndex];
      event.container.data[event.currentIndex] = {
        ...transferredItem,
        col: transferredItem.col,
        recommended: false,
        overridden_recommended: false,
        final_recommended: false,
        dri: transferredItem.dri || 100,
        dqi: transferredItem.dqi || 0,
      };
      console.log('Updated relevant column data:', event.container.data);
    } else if (fromRelevant && toNonRelevant) {
      console.log('Moving from relevant to non-relevant');
      // Perform the transfer
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
      const transferredItem = event.container.data[event.currentIndex];
      event.container.data[event.currentIndex] = {
        ...transferredItem,
        col: transferredItem.col,
        recommended: false,
        overridden_recommended: false,
        final_recommended: false,
        dri: transferredItem.dri || 0,
        dqi: transferredItem.dqi || 0,
      };

      // Ensure nonRelevantColumns is updated and Angular detects the change
      this.nonRelevantColumns = [...event.container.data];
      console.log('Updated nonRelevantColumns:', this.nonRelevantColumns);
    }

    console.log('Final columns state:', this.columns);
    console.log('Final nonRelevantColumns state:', this.nonRelevantColumns);
  }
  dragStarted(event: CdkDragStart) {
    this.dragSourceId = event.source.dropContainer.id; // Store the ID of the container where drag started
    console.log('Drag started from:', this.dragSourceId);
  }

  dragMoved(event: CdkDragMove) {
    const pointerX = event.pointerPosition.x;
    const pointerY = event.pointerPosition.y;

    // Only expand relevant panel if drag started from non-relevant
    const relevantHeaderRect = this.relevantPanelHeader.nativeElement.getBoundingClientRect();
    if (
      pointerX >= relevantHeaderRect.left &&
      pointerX <= relevantHeaderRect.right &&
      pointerY >= relevantHeaderRect.top &&
      pointerY <= relevantHeaderRect.bottom &&
      !this.relevantPanel.expanded &&
      this.dragSourceId === 'non-relevant' // Only expand if dragging from non-relevant
    ) {
      console.log('Expanding relevant panel on drag over header (from non-relevant)');
      this.relevantPanel.open();
    }

    // Only expand non-relevant panel if drag started from relevant
    const nonRelevantHeaderRect = this.nonRelevantPanelHeader.nativeElement.getBoundingClientRect();
    if (
      pointerX >= nonRelevantHeaderRect.left &&
      pointerX <= nonRelevantHeaderRect.right &&
      pointerY >= nonRelevantHeaderRect.top &&
      pointerY <= nonRelevantHeaderRect.bottom &&
      !this.nonRelevantPanel.expanded &&
      this.dragSourceId !== null &&
      this.dragSourceId.startsWith('relevant') // Only expand if dragging from relevant
    ) {
      console.log('Expanding non-relevant panel on drag over header (from relevant)');
      this.nonRelevantPanel.open();
    }

    // Auto-expand target column only if dragging from non-relevant to relevant
    this.columns.forEach(column => {
      const columnElement = document.getElementById(column.id);
      if (columnElement) {
        const rect = columnElement.getBoundingClientRect();
        if (
          pointerX >= rect.left &&
          pointerX <= rect.right &&
          pointerY >= rect.top &&
          pointerY <= rect.bottom &&
          !column.expanded &&
          this.dragSourceId === 'non-relevant' // Only expand if dragging from non-relevant
        ) {
          console.log(`Expanding column ${column.name} on drag over (from non-relevant)`);
          this.toggleColumn(column);
        }
      }
    });

    // Scroll the relevant accordion’s content (only if dragging from non-relevant)
    const relevantScrollContainer = this.scrollContainers.find(container =>
      container.nativeElement.closest('mat-expansion-panel')?.id === this.relevantPanel.id
    );

    if (relevantScrollContainer && this.relevantPanel.expanded && this.dragSourceId === 'non-relevant') {
      const container = relevantScrollContainer.nativeElement;
      const rect = container.getBoundingClientRect();
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;

      const topThreshold = rect.top + 10;
      const bottomThreshold = rect.bottom - 10;
      this.scrollSpeed = 20;

      if (pointerY < topThreshold && scrollTop > 0) {
        if (!this.scrollInterval) {
          this.scrollInterval = setInterval(() => {
            container.scrollTop -= this.scrollSpeed;
            if (container.scrollTop <= 0) {
              clearInterval(this.scrollInterval);
              this.scrollInterval = null;
            }
          }, 20);
        }
      } else if (pointerY > bottomThreshold && scrollTop + clientHeight < scrollHeight) {
        if (!this.scrollInterval) {
          this.scrollInterval = setInterval(() => {
            container.scrollTop += this.scrollSpeed;
            if (container.scrollTop + clientHeight >= scrollHeight) {
              clearInterval(this.scrollInterval);
              this.scrollInterval = null;
            }
          }, 20);
        }
      } else {
        this.stopScrolling();
      }
    }
  }
  dragReleased(event: CdkDragRelease) {
    console.log('Drag released');
    this.stopScrolling();
    this.dragSourceId = null; // Reset drag source
  }

  private stopScrolling() {
    if (this.scrollInterval) {
      clearInterval(this.scrollInterval);
      this.scrollInterval = null;
    }
  }

  isChecked(columnName: string, subColName: string): boolean {
    const column = this.columns.find(col => col.name === columnName);
    if (column) {
      const subCol = column.subColumns.find(sub => sub.col === subColName);
      if (subCol) {
        const hasOverwritten = column.subColumns.some(sub => sub.overridden_recommended);
        if (hasOverwritten) {
          return subCol.overridden_recommended;
        } else {
          return subCol.final_recommended;
        }
      }
    }
    return false;
  }

  onCheckboxChange(event: any, columnName: string, subColName: string) {
    const column = this.columns.find(col => col.name === columnName);
    if (column) {
      const subCol = column.subColumns.find(sub => sub.col === subColName);
      if (subCol) {
        if (event.checked) {
          console.log(event.checked)
          column.subColumns.forEach((sub: any) => {
            sub.overridden_recommended = false;
          });
          console.log(column.subColumns)
          subCol.overridden_recommended = true;
          console.log(`Set ${subColName} in ${columnName} as overwritten recommended`);
        } else {
          const hasOverwritten = column.subColumns.some(sub => sub.overridden_recommended);
          if (hasOverwritten && subCol.overridden_recommended) {
            subCol.overridden_recommended = true;
            console.log(`Cannot uncheck ${subColName} in ${columnName}; one must remain selected`);
          } else if (!hasOverwritten) {
            const recommendedCount = column.subColumns.filter(sub => sub.final_recommended).length;
            if (recommendedCount === 1 && subCol.final_recommended) {
              subCol.overridden_recommended = false;
              console.log(`Cannot uncheck ${subColName} in ${columnName}; it’s the only recommended`);
            } else {
              subCol.overridden_recommended = false;
              console.log(`Unset ${subColName} in ${columnName} as overwritten recommended`);
            }
          }
        }
      }
    }
  }

  formatFileName(fileName: string): string {
    return fileName
      .replace(/^[^_]+_/, '')
      .replace(/_/g, ' ')
      .replace(/\.\w+$/, '')
      .replace(/\b\w/g, char => char.toUpperCase());
  }


  toggleColumn(selectedColumn: any) {
    this.columns.forEach(column => {
      column.expanded = (column === selectedColumn) ? !column.expanded : false;
    });
  }

  goBack() {
    window.history.back();
  }

  onSelect(event: any) {
    console.log('Item clicked', event);
  }

  onActivate(event: any) {
    this.hoveredBar = event.value;
    window.addEventListener('mousemove', this.updateTooltipPosition);
  }

  onDeactivate(event: any) {
    this.hoveredBar = null;
    window.removeEventListener('mousemove', this.updateTooltipPosition);
  }

  toggleExpand() {
    this.isExpanded = !this.isExpanded;
  }

  updateTooltipPosition = (event: MouseEvent) => {
    this.tooltipPosition = {
      x: event.clientX + 10,
      y: event.clientY + 10
    };
  };

  //  Download() {
  //     const sessionid: string = localStorage.getItem('sessionid') || ''; 

  //     if (!sessionid) {
  //       console.error('Session ID is missing');
  //       return;
  //     }

  //     this.exploreModeService.downloadTTMMap(sessionid).subscribe({
  //       next: (response: TTMMapResponse) => {
  //         if (!response.ttm_map) {
  //           console.error('ttm_map is missing in the response:', response);
  //           return;
  //         }
  //         this.dialog.open(EditTtmDialogComponent, {
  //           // width: '1400px',
  //           maxHeight: '80vh',
  //           data: response.ttm_map
  //         });
  //         // console.log('Dialog opened with width: 1400px');

  //       },
  //       error: (err) => {
  //         console.error('Failed to fetch JSON:', err);
  //       },
  //     });
  //   }

  // handleApiPayload(apiPayload: any) {
  //   this.apiPayloadFromDialog = apiPayload;
  //   const sessionid: string = localStorage.getItem('sessionid') || ''; 
  //   // Here you can call uploadService or downloadTTMMap or any other service:
  //   // For example:
  //   this.exploreModeService.downloadTTMMap(apiPayload, sessionid).subscribe({
  //     next: (result: any) => {
  //       console.log('Downloaded with updated TTM Map:', result);
  //       // Handle download here
  //     },
  //     error: (err:any) => {
  //       console.error('Download error:', err);
  //     }
  //   });
  // }

  EditTTM() {
    const sessionid: string = localStorage.getItem('sessionid') || '';
    if (!sessionid) {
      console.error('Session ID is missing');
      return;
    }

    const selectedFileName = this.selectedFile.value ?? '';
    this.exploreModeService.downloadTTMMap(sessionid).subscribe({
      next: (response: TTMMapResponse) => {
        if (!response.ttm_map) {
          console.error('ttm_map is missing in the response:', response);
          return;
        }

        // 🗂️ Store the entire response for later download
        this.ttmMapResponse = response;

        const selectedFileKey = this.getMappedFileName(selectedFileName);
        const selectedFileMapData = response.ttm_map[selectedFileKey];

        if (!selectedFileMapData) {
          console.error(`No TTM map data found for file: ${selectedFileKey}`);
          return;
        }

        const dialogRef = this.dialog.open(EditTtmDialogComponent, {
          maxHeight: '80vh',
          width: '1000px',
          maxWidth: 'none',
          panelClass: 'custom-dialog-container',
          data: {
            [selectedFileKey]: selectedFileMapData
          }
        });

        // dialogRef.afterClosed().subscribe((apiPayload: any) => {
        //   if (apiPayload) {
        //     console.log('Received apiPayload from dialog:', apiPayload);
        //     this.handleApiPayload(apiPayload);
        //   }
        // });
      },
      error: (err) => {
        console.error('Failed to fetch JSON:', err);
      },
    });
  }


  Download() {
    const sessionid: string = localStorage.getItem('sessionid') || '';
    if (!sessionid) {
      console.error('Session ID is missing');
      return;
    }

    const selectedFileName = this.selectedFile.value ?? '';

    // 🔥 Make the same API call as EditTTM
    this.exploreModeService.downloadTTMMap(sessionid).subscribe({
      next: (response: TTMMapResponse) => {
        if (!response.ttm_map) {
          console.error('ttm_map is missing in the response:', response);
          return;
        }

        // 🗂️ Store the entire response for later use
        this.ttmMapResponse = response;

        // 🔽 Download the response as JSON
        const blob = new Blob([JSON.stringify(this.ttmMapResponse, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'ttm_map.json'; // You can name this file as desired
        a.click();
        window.URL.revokeObjectURL(url);

        console.log('Downloaded TTM Map JSON successfully!');
      },
      error: (err) => {
        console.error('Failed to fetch JSON:', err);
      }
    });
  }





  updateDqiNDri() {
    this.isButtonDisabled = true;
    this.isLoading = true;
    const sessionid: any = localStorage.getItem('sessionid')
    const formData = new FormData();
    formData.append('DEXCAT_MODE', 'EXPLORE');
    formData.append('session_id', sessionid);
    formData.append('dri_threshold', this.driThreshold);
    formData.append('dqi_threshold', this.dqiThreshold);
    this.uploadService.uploadFiles(formData).subscribe({
      next: (response) => {
        this.uploadService.setResponseData(response);
        this.initialize();
        this.isLoading = false;
      }
    })

  }

}
